import { useAddressLabelsForCountry } from "@certn/cascadia/patterns/address-form/hooks";
import {
  Address,
  AddressErrors,
} from "@certn/cascadia/patterns/address-form/types";
import {
  filterSegment,
  initialAddressValues,
} from "@certn/cascadia/patterns/address-form/utils";
import { Key, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddressTextField } from "../../components/inputs/select/AddressTextField";
import { GridItem } from "../../foundations/grid-layout/GridItem";

import { AdministrativeDivision2Select } from "./AdministrativeDivision2Select";
import { AdministrativeDivisionSelect } from "./AdministrativeDivisionSelect";
import { CountrySelect } from "./CountrySelect";
import { TextField } from "../../components/inputs/text-field/TextField";
import { normalizeUsCountyName } from "@certn/utils";

interface AddressFormProps {
  isWithoutUnit?: boolean;
  onChange: (e: Address) => void;
  value: Address;
  errors?: AddressErrors;
  isSubmitted?: boolean;
  onBlur?: (field: string) => void;
  testID: string;
  isDisabled?: boolean;
  postalCodeRequired?: boolean;
  streetAddressRequired?: boolean;
  unitNumberRequired?: boolean;
  localityRequired?: boolean;
  administrativeDivision1Required?: boolean;
  administrativeDivision2Required?: boolean;
  showStreetAddress?: boolean;
  showUnitNumber?: boolean;
  countryRequired?: boolean;
  context?: string;
}

export function AddressForm(props: AddressFormProps) {
  const {
    isWithoutUnit,
    value = initialAddressValues,
    errors = initialAddressValues,
    isSubmitted,
    onChange,
    testID,
    isDisabled,
    postalCodeRequired = true,
    streetAddressRequired = true,
    unitNumberRequired = false,
    localityRequired = true,
    administrativeDivision1Required = false,
    administrativeDivision2Required = true,
    showStreetAddress = true,
    showUnitNumber = true,
    countryRequired = true,
  } = props;

  const { t } = useTranslation();
  const labels = useAddressLabelsForCountry(value.country);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );

  const handleAddressSelect = (place: google.maps.places.PlaceResult) => {
    const { address_components: address, utc_offset_minutes } = place;
    const country = filterSegment(address, ["country"]);
    const unit = filterSegment(address, ["subpremise", "premise"]);
    const streetNumber = filterSegment(address, ["number", "street_number"]);
    const streetName = filterSegment(address, ["route"]);
    const streetAddress = streetNumber
      ? `${streetNumber?.short_name} ${streetName?.short_name}`
      : streetName?.short_name;

    const locality = filterSegment(address, [
      "locality",
      "sublocality",
      "postal_town",
    ]);
    const admininstrativeDivision = filterSegment(address, [
      "administrative_area_level_1",
    ]);
    const administrative_division_2 = filterSegment(address, [
      "administrative_area_level_2",
    ]);
    const postalCode = filterSegment(address, ["postal_code"]);

    // UK short_name returned from Google must match UK subdivisions provided by API endpoint
    const shortNameConversion = (shortName: string) => {
      const ukLongNameToShortName = {
        England: "ENG",
        Wales: "WLS",
        Scotland: "SCT",
        "Northern Ireland": "NIR",
      };

      if (Object.keys(ukLongNameToShortName).includes(shortName)) {
        return ukLongNameToShortName[
          shortName as keyof typeof ukLongNameToShortName
        ];
      }

      return shortName;
    };

    onChange({
      street_address: streetAddress,
      country: country?.short_name || "",
      unit_number: unit?.short_name || "",
      locality: locality?.long_name || "",
      administrative_division_2: normalizeUsCountyName(
        country?.short_name || "",
        administrative_division_2?.short_name || ""
      ),
      administrative_division_1: shortNameConversion(
        admininstrativeDivision?.short_name || ""
      ),
      postal_code: postalCode?.long_name || "",
      timezone: utc_offset_minutes,
    });
  };

  const touchedErrors: Record<string, string> = Object.entries(errors).reduce(
    (acc, [key, error]) => {
      return {
        ...acc,
        [key]: touchedFields[key] || isSubmitted ? error : undefined,
      };
    },
    {}
  );
  const handleBlurred = (field: string) => {
    setTouchedFields({ ...touchedFields, [field]: true });
    props.onBlur && props.onBlur(field);
  };

  return (
    <>
      <CountrySelect
        onBlur={() => handleBlurred("country")}
        errors={touchedErrors.country}
        isInvalid={Boolean(touchedErrors.country)}
        selectedKey={value.country}
        onChange={(country) => {
          onChange({
            ...value,
            country,
            // Clear administrative_division_2 when country changes to/from US
            administrative_division_2: "",
          });
        }}
        isRequired={countryRequired}
        label={t("Country")}
        testID={`${testID}-country-select`}
        isDisabled={isDisabled}
      />
      {showStreetAddress && (
        <AddressTextField
          onBlur={() => handleBlurred("street_address")}
          errors={touchedErrors.street_address || touchedErrors.address_line_1}
          isInvalid={Boolean(
            touchedErrors.street_address || touchedErrors.address_line_1
          )}
          inputValue={value.street_address ?? ""}
          onInputChange={(streetAddress) => {
            onChange({ ...value, street_address: streetAddress });
          }}
          onAddressSelected={handleAddressSelect}
          isRequired={streetAddressRequired}
          label={t("Address")}
          testID={`${testID}-address-input`}
          defaultInputValue={value.street_address ?? ""}
          isDisabled={isDisabled}
        />
      )}
      {!isWithoutUnit && showUnitNumber && (
        <TextField
          onBlur={() => handleBlurred("unit_number")}
          errors={touchedErrors.unit_number}
          isInvalid={Boolean(touchedErrors.unit_number)}
          value={value.unit_number ?? ""}
          onChange={(unitNumber) => {
            onChange({ ...value, unit_number: unitNumber });
          }}
          isRequired={unitNumberRequired}
          label={t("Apartment or unit number")}
          testID={`${testID}-unit-input`}
          isDisabled={isDisabled}
        />
      )}
      <TextField
        onBlur={() => handleBlurred("locality")}
        errors={touchedErrors.locality}
        isInvalid={Boolean(touchedErrors.locality)}
        value={value.locality ?? ""}
        onChange={(locality) => {
          onChange({ ...value, locality });
        }}
        isRequired={localityRequired}
        label={labels.locality}
        testID={`${testID}-locality-input`}
        isDisabled={isDisabled}
      />

      <GridItem span={8}>
        <AdministrativeDivisionSelect
          onBlur={() => handleBlurred("administrative_division_1")}
          errors={touchedErrors.administrative_division_1}
          isInvalid={Boolean(touchedErrors.administrative_division_1)}
          country={value.country}
          selectedKey={value.administrative_division_1}
          onChange={(administrativeDivision: Key | null) => {
            onChange({
              ...value,
              administrative_division_1: administrativeDivision as string,
              // Clear administrative_division_2 when state changes for US addresses
              administrative_division_2:
                value.country === "US" ? "" : value.administrative_division_2,
            });
          }}
          label={labels.administrative_division_1}
          testID={`${testID}-administrative-division-input`}
          isDisabled={isDisabled}
          isRequired={administrativeDivision1Required}
        />
      </GridItem>
      <AdministrativeDivision2Select
        onBlur={() => handleBlurred("administrative_division_2")}
        errors={touchedErrors.administrative_division_2}
        isInvalid={Boolean(touchedErrors.administrative_division_2)}
        country={value.country}
        administrativeDivision1={value.administrative_division_1}
        administrativeDivision2={value.administrative_division_2}
        onChange={(administrative_division_2) => {
          onChange({
            ...value,
            administrative_division_2,
          });
        }}
        isRequired={administrativeDivision2Required}
        label={labels.administrative_division_2}
        testID={`${testID}`}
        isDisabled={isDisabled}
        context={props.context}
      />
      <GridItem span={4}>
        <TextField
          onBlur={() => handleBlurred("postal_code")}
          errors={touchedErrors.postal_code}
          isInvalid={Boolean(touchedErrors.postal_code)}
          value={value.postal_code ?? ""}
          onChange={(postalCode: string) => {
            onChange({ ...value, postal_code: postalCode });
          }}
          isRequired={postalCodeRequired}
          label={labels.postal_code}
          testID={`${testID}-postal-code-input`}
          isDisabled={isDisabled}
        />
      </GridItem>
    </>
  );
}
